<template>
  <div v-if="internalCustomer" class="customer-form-wrap">
    <div class="settings-section">
      <div class="settings-section__header">
        <div class="section-icon section-icon--blue">
          <i class="el-icon-info"></i>
        </div>
        <h3 class="settings-section__title">{{ $t('settings.sectionINFO') }}</h3>
      </div>

      <el-form
        class="customer-edit-form"
        ref="general-form"
        @submit.native.prevent="onGeneralFormSubmit"
        :rules="formRules"
        :model="internalCustomer"
        label-position="top">

        <div class="form-grid">
          <el-form-item :label="$t('settings.name')" :error="error" prop="name">
            <el-input v-model="internalCustomer.name" prefix-icon="el-icon-office-building"></el-input>
          </el-form-item>

          <el-form-item :label="$t('settings.mail')" prop="mail">
            <el-input v-model="internalCustomer.mail" prefix-icon="el-icon-message"></el-input>
          </el-form-item>

          <el-form-item :label="$t('settings.appLoginDomain')" :error="error" prop="appLoginDomain">
            <el-input v-model="internalCustomer.appLoginDomain" prefix-icon="el-icon-link"></el-input>
          </el-form-item>
        </div>

        <el-form-item class="checkbox-item">
          <el-checkbox v-model="internalCustomer.default">
            {{ $t('settings.defaultCustomer') }}
          </el-checkbox>
        </el-form-item>

        <div class="form-actions">
          <el-button
            v-if="mode === 'edit'"
            type="primary"
            class="btn-submit"
            :loading="loading"
            @click="onGeneralFormSubmit">
            <i class="el-icon-check"></i> {{ $t('settings.save') }}
          </el-button>
          <el-button
            v-if="mode === 'create'"
            type="success"
            class="btn-submit"
            :loading="loading"
            @click="onGeneralFormSubmit">
            <i class="el-icon-plus"></i> {{ $t('settings.create') }}
          </el-button>
        </div>
      </el-form>
    </div>

    <template v-if="mode === 'edit'">
      <div class="ldap-section">
        <div class="ldap-section__header">
          <div class="section-icon section-icon--purple">
            <i class="el-icon-connection"></i>
          </div>
          <div class="ldap-section__title-wrap">
            <h3 class="ldap-section__title">{{ $t('settings.sectionLDAP') }}</h3>
            <span class="ldap-section__count">{{ ldapProviders.length }} {{ connectionCountLabel }}</span>
          </div>
          <el-button
            v-if="ldapProviders.length > 0"
            type="primary"
            plain
            icon="el-icon-plus"
            size="small"
            class="btn-add-connection"
            :loading="ldapLoading"
            @click="onCreateConnectionClick()">
            {{ $t('settings.addConnection') }}
          </el-button>
        </div>

        <!-- Empty state -->
        <div v-if="!ldapLoading && ldapProviders.length === 0" class="ldap-empty">
          <div class="ldap-empty__icon">
            <i class="el-icon-connection"></i>
          </div>
          <p class="ldap-empty__title">{{ $t('settings.ldapEmptyTitle') }}</p>
          <p class="ldap-empty__subtitle">{{ $t('settings.ldapEmptySubtitle') }}</p>
          <el-button type="primary" size="small" icon="el-icon-plus" @click="onCreateConnectionClick()">
            {{ $t('settings.addConnection') }}
          </el-button>
        </div>

        <!-- Connections table -->
        <base-table
          v-if="ldapProviders.length > 0"
          :data="ldapProviders"
          :loading="ldapLoading"
          :empty-text="$t('settings.noConnections')"
          row-class-name="ad-connection-table-row"
          :row-style="{ cursor: 'pointer' }"
          class="ldap-table"
          @row-click="onRowClick">

          <el-table-column :label="$t('settings.url')" min-width="200">
            <template v-slot="scope">
              <div class="cell-url">
                <i class="el-icon-link cell-url__icon"></i>
                <span class="cell-url__text">
                  {{ scope.row.server ? scope.row.server.url : scope.row.serverId }}
                </span>
              </div>
            </template>
          </el-table-column>

          <el-table-column :label="$t('settings.base')" min-width="200">
            <template v-slot="scope">
              <span class="cell-dn">{{ scope.row.distinguishedName }}</span>
            </template>
          </el-table-column>

          <el-table-column :label="$t('settings.authority')" width="130" align="center">
            <template v-slot="scope">
              <el-tag size="small" class="role-tag" :type="roleTagType(scope.row.role)">
                {{ scope.row.role }}
              </el-tag>
            </template>
          </el-table-column>

          <el-table-column :label="$t('settings.userCount')" width="160" align="center">
            <template v-slot="scope">
              <el-tag type="info" size="mini" class="domain-tag">
                <i class="el-icon-user"></i> {{ scope.row.synchronizedUserCount || 0 }}
              </el-tag>
            </template>
          </el-table-column>

          <el-table-column width="100" align="center">
            <template v-slot="scope">
              <div class="action-btns">
                <el-tooltip :content="$t('settings.edit')" placement="top">
                  <el-button
                    @click.native.stop="onRowClick(scope.row)"
                    type="primary"
                    icon="el-icon-edit"
                    size="mini"
                    circle>
                  </el-button>
                </el-tooltip>
                <el-tooltip :content="$t('base.delete')" placement="top">
                  <el-button
                    @click.native.stop="onAdConnectionDeleteIntentionClick(scope.row)"
                    type="danger"
                    icon="el-icon-delete"
                    size="mini"
                    circle>
                  </el-button>
                </el-tooltip>
              </div>
            </template>
          </el-table-column>
        </base-table>
      </div>

      <el-dialog
        :visible.sync="showConnectionModal"
        @close="onDialogClose()"
        width="500px"
        :show-close="false"
        custom-class="customer-connection-dialog">

        <div slot="title" class="connection-dialog__header">
          <div class="connection-dialog__icon">
            <i class="el-icon-connection"></i>
          </div>
          <div class="connection-dialog__title-wrap">
            <span class="connection-dialog__title">
              {{
                currentProvider && currentProvider.id ? $t('settings.adConnections.edit') : $t('settings.addConnection')
              }}
            </span>
            <span class="connection-dialog__subtitle">{{ $t('settings.connectionDialogSubtitle') }}</span>
          </div>
          <button class="connection-dialog__close" @click="showConnectionModal = false">
            <i class="el-icon-close"></i>
          </button>
        </div>

        <div class="dialog-body" v-if="currentProvider">
          <el-form
            @submit.native.prevent="onConnectionFormSubmit"
            :model="currentProvider"
            ref="connection-form"
            label-position="top">

            <el-form-item
              :label="$t('settings.url')"
              prop="serverId"
              :rules="[{ required: true, message: $t('settings.customerRules.url'), trigger: 'change' }]">
              <el-select v-model="currentProvider.serverId" style="width:100%"
                         :placeholder="$t('settings.selectServer')">
                <el-option
                  v-for="server in ldapServers"
                  :key="server.id"
                  :label="server.url"
                  :value="server.id">
                  <div class="server-option">
                    <i class="el-icon-monitor server-option__icon"></i>
                    <span class="server-option__url">{{ server.url }}</span>
                    <el-tag v-if="server.tenantCode || server.code" size="mini" class="server-option__badge">
                      {{ server.tenantCode || server.code }}
                    </el-tag>
                  </div>
                </el-option>
              </el-select>
            </el-form-item>

            <el-form-item
              :label="$t('settings.base')"
              prop="distinguishedName"
              :rules="[{ required: true, message: $t('settings.customerRules.user'), trigger: 'blur' }]">
              <el-input
                v-model="currentProvider.distinguishedName"
                :placeholder="$t('settings.dnPlaceholder')"
                prefix-icon="el-icon-folder">
              </el-input>
            </el-form-item>

            <el-form-item
              :label="$t('settings.authority')"
              prop="role"
              :rules="[{ required: true, message: $t('settings.customerRules.role'), trigger: 'change' }]">
              <div class="role-cards">
                <div
                  v-for="role in roles"
                  :key="role.value"
                  class="role-card"
                  :class="{ 'role-card--active': currentProvider.role === role.value }"
                  @click="currentProvider.role = role.value">
                  <i :class="role.icon" class="role-card__icon"></i>
                  <span class="role-card__name">{{ role.label }}</span>
                  <span class="role-card__desc">{{ role.desc }}</span>
                </div>
              </div>
            </el-form-item>
          </el-form>
        </div>

        <div slot="footer" class="connection-dialog__footer connection-dialog__footer--split">
          <div class="connection-dialog__footer-left">
            <base-test-connection-button
              :validate="() => $refs['connection-form'].validate()"
              :request="validateLdapProviderRequest"
              success-key="valid"
              :fail-message="$t('settings.adConnections.ldapServerErrors.noMatchingUser')"
              :error-formatter="ldapErrorMessage"/>
          </div>
          <div class="connection-dialog__footer-right">
            <el-button @click="showConnectionModal = false">{{ $t('base.cancel') }}</el-button>
            <el-button type="primary" @click="onConnectionFormSubmit" :loading="ldapLoading">
              <i class="el-icon-check"></i> {{ $t('settings.save') }}
            </el-button>
          </div>
        </div>
      </el-dialog>

      <!-- ── Delete Connection Dialog ── -->
      <base-delete-dialog
        :visible.sync="showDeleteConnectionModal"
        :title="$t('settings.adConnections.deleteConnectionTitle')"
        width="400px"
        :loading="ldapLoading"
        @confirm="onConnectionDeleteConfirmation">
        <template v-if="currentProviderForDeletion">
          {{
            $t('settings.adConnections.deleteConnectionInfoText', {
              synced: syncedCount(currentProviderForDeletion)
            })
          }}
        </template>
      </base-delete-dialog>

      <!-- Entra -->
      <div class="ldap-section entra-section">
        <div class="ldap-section__header">
          <div class="section-icon section-icon--entra">
            <i class="el-icon-cloudy"></i>
          </div>
          <div class="ldap-section__title-wrap">
            <h3 class="ldap-section__title">{{ $t('settings.sectionEntra') }}</h3>
            <span class="ldap-section__count">{{ entraProviders.length }} {{ entraCountLabel }}</span>
          </div>
          <el-button
            v-if="entraProviders.length > 0"
            type="primary"
            plain
            icon="el-icon-plus"
            size="small"
            class="btn-add-connection"
            :loading="entraLoading"
            @click="onCreateEntraProviderClick()">
            {{ $t('settings.addConnection') }}
          </el-button>
        </div>

        <!-- Empty state -->
        <div v-if="!entraLoading && entraProviders.length === 0" class="ldap-empty">
          <div class="ldap-empty__icon ldap-empty__icon--entra">
            <i class="el-icon-cloudy"></i>
          </div>
          <p class="ldap-empty__title">{{ $t('settings.entraEmptyTitle') }}</p>
          <p class="ldap-empty__subtitle">{{ $t('settings.entraEmptySubtitle') }}</p>
          <el-button type="primary" size="small" icon="el-icon-plus" @click="onCreateEntraProviderClick()">
            {{ $t('settings.addConnection') }}
          </el-button>
        </div>

        <!-- Providers table -->
        <base-table
          v-if="entraProviders.length > 0"
          :data="entraProviders"
          :loading="entraLoading"
          :empty-text="$t('settings.noEntraConnections')"
          row-class-name="ad-connection-table-row"
          :row-style="{ cursor: 'pointer' }"
          class="ldap-table"
          @row-click="onEntraRowClick">

          <el-table-column :label="$t('settings.entraTenantNameColumn')" min-width="200">
            <template v-slot="scope">
              <div class="cell-url">
                <i class="el-icon-cloudy cell-url__icon cell-url__icon--entra"></i>
                <span class="cell-url__text">{{ entraTenantName(scope.row) }}</span>
              </div>
            </template>
          </el-table-column>

          <el-table-column v-if="isSuperAdmin" :label="$t('settings.tenant.name')" min-width="200">
            <template v-slot="scope">
              <span class="cell-dn">{{ entraTenantLabel(scope.row) }}</span>
            </template>
          </el-table-column>

          <el-table-column :label="$t('settings.entraTenantId')" min-width="200">
            <template v-slot="scope">
              <div class="cell-url">
                <i class="el-icon-cloudy cell-url__icon cell-url__icon--entra"></i>
                <span class="cell-url__text">{{ entraConnectionLabel(scope.row) }}</span>
              </div>
            </template>
          </el-table-column>

          <el-table-column :label="$t('settings.entraGroup')" min-width="200">
            <template v-slot="scope">
              <span class="cell-dn">{{ entraGroupLabel(scope.row) }}</span>
            </template>
          </el-table-column>

          <el-table-column :label="$t('settings.authority')" width="130" align="center">
            <template v-slot="scope">
              <el-tag size="small" class="role-tag" :type="roleTagType(scope.row.role)">
                {{ scope.row.role }}
              </el-tag>
            </template>
          </el-table-column>

          <el-table-column :label="$t('settings.userCount')" width="160" align="center">
            <template v-slot="scope">
              <el-tag type="info" size="mini" class="domain-tag">
                <i class="el-icon-user"></i> {{ scope.row.synchronizedUserCount || 0 }}
              </el-tag>
            </template>
          </el-table-column>

          <el-table-column width="100" align="center">
            <template v-slot="scope">
              <div class="action-btns">
                <el-tooltip :content="$t('settings.edit')" placement="top">
                  <el-button
                    @click.native.stop="onEntraRowClick(scope.row)"
                    type="primary"
                    icon="el-icon-edit"
                    size="mini"
                    circle>
                  </el-button>
                </el-tooltip>
                <el-tooltip :content="$t('base.delete')" placement="top">
                  <el-button
                    @click.native.stop="onEntraProviderDeleteIntentionClick(scope.row)"
                    type="danger"
                    icon="el-icon-delete"
                    size="mini"
                    circle>
                  </el-button>
                </el-tooltip>
              </div>
            </template>
          </el-table-column>
        </base-table>
      </div>

      <!-- Entra Provider Create / Edit Dialog -->
      <el-dialog
        :visible.sync="showEntraModal"
        @close="onEntraDialogClose()"
        width="500px"
        :show-close="false"
        custom-class="customer-connection-dialog">

        <div slot="title" class="connection-dialog__header">
          <div class="connection-dialog__icon connection-dialog__icon--entra">
            <i class="el-icon-cloudy"></i>
          </div>
          <div class="connection-dialog__title-wrap">
            <span class="connection-dialog__title">
              {{
                currentEntraProvider && currentEntraProvider.id ? $t('settings.entraConnectionEdit') : $t('settings.addConnection')
              }}
            </span>
            <span class="connection-dialog__subtitle">{{ $t('settings.entraProviderDialogSubtitle') }}</span>
          </div>
          <button class="connection-dialog__close" @click="showEntraModal = false">
            <i class="el-icon-close"></i>
          </button>
        </div>

        <div class="dialog-body" v-if="currentEntraProvider">
          <el-form
            @submit.native.prevent="onEntraProviderFormSubmit"
            :model="currentEntraProvider"
            ref="entra-provider-form"
            label-position="top">

            <!-- Connection Dropdown-->
            <el-form-item
              :label="$t('settings.entraTenantColumn')"
              prop="connectionId"
              :rules="[{ required: true, message: $t('settings.entraRules.connection'), trigger: 'change' }]">
              <el-select
                v-model="currentEntraProvider.connectionId"
                style="width:100%"
                popper-class="entra-connection-select"
                :placeholder="$t('settings.selectConnection')"
                @change="onEntraConnectionChange">
                <el-option
                  v-for="connection in entraConnections"
                  :key="connection.id"
                  :label="connection.name"
                  :value="connection.id">
                  <div class="server-option">
                    <i class="el-icon-cloudy server-option__icon"></i>
                    <div class="server-option__text">
                      <span class="server-option__title">{{ connection.name || connection.entraTenantId }}</span>
                      <span v-if="connection.name" class="server-option__subtitle">{{ connection.entraTenantId }}</span>
                    </div>
                  </div>
                </el-option>
              </el-select>
            </el-form-item>

            <!-- Group Dropdown, Sync Button -->
            <el-form-item
              :label="$t('settings.entraGroup')"
              prop="groupId"
              :rules="[{ required: true, message: $t('settings.entraRules.group'), trigger: 'change' }]">
              <div class="group-select-row">
                <el-select
                  v-model="currentEntraProvider.groupId"
                  class="group-select-row__select"
                  :disabled="!currentEntraProvider.connectionId"
                  :loading="entraGroupsLoading"
                  :placeholder="currentEntraProvider.connectionId ? $t('settings.selectGroup') : $t('settings.entraGroupSelectFirst')">
                  <el-option
                    v-for="group in entraGroups"
                    :key="group.id"
                    :label="group.name || group.displayName"
                    :value="group.id">
                  </el-option>
                </el-select>
                <el-tooltip :content="$t('settings.entraGroupSync')" placement="top">
                  <span>
                    <el-button
                      class="group-select-row__sync"
                      type="primary"
                      icon="el-icon-refresh"
                      circle
                      :disabled="!currentEntraProvider.connectionId || entraGroupsSyncing"
                      :loading="entraGroupsSyncing"
                      @click="onSyncEntraGroupsClick">
                    </el-button>
                  </span>
                </el-tooltip>
              </div>
            </el-form-item>

            <!-- Role Cards-->
            <el-form-item
              :label="$t('settings.authority')"
              prop="role"
              :rules="[{ required: true, message: $t('settings.customerRules.role'), trigger: 'change' }]">
              <div class="role-cards">
                <div
                  v-for="role in roles"
                  :key="role.value"
                  class="role-card"
                  :class="{ 'role-card--active': currentEntraProvider.role === role.value }"
                  @click="currentEntraProvider.role = role.value">
                  <i :class="role.icon" class="role-card__icon"></i>
                  <span class="role-card__name">{{ role.label }}</span>
                  <span class="role-card__desc">{{ role.desc }}</span>
                </div>
              </div>
            </el-form-item>
          </el-form>
        </div>

        <div slot="footer" class="connection-dialog__footer connection-dialog__footer--split">
          <div class="connection-dialog__footer-left">
            <base-test-connection-button
              :validate="() => $refs['entra-provider-form'].validate()"
              :request="validateEntraProviderRequest"
              success-key="valid"
              :fail-message="$t('settings.entraErrors.validationFailed')"
              :error-formatter="entraProviderTestError"/>
          </div>
          <div class="connection-dialog__footer-right">
            <el-button @click="showEntraModal = false">{{ $t('base.cancel') }}</el-button>
            <el-button type="primary" @click="onEntraProviderFormSubmit" :loading="entraLoading">
              <i class="el-icon-check"></i> {{ $t('settings.save') }}
            </el-button>
          </div>
        </div>
      </el-dialog>

      <!-- Delete Entra Provider Dialog -->
      <base-delete-dialog
        :visible.sync="showEntraDeleteModal"
        :title="$t('settings.entraDeleteConnectionTitle')"
        width="400px"
        :loading="entraLoading"
        @confirm="onEntraProviderDeleteConfirmation">
        <template v-if="currentEntraProviderForDeletion">
          {{ $t('settings.entraDeleteProviderInfoText') }}
        </template>
      </base-delete-dialog>
    </template>
  </div>
</template>

<script>
import {generateRules} from "@/helper/CustomerSettingsHelper";
import {getEntraErrorMessage} from "@/helper/EntraErrorHelper";
import {ldapAccountProviderService} from "@/services/LdapAccountProviderService.class";
import {ldapServerService} from "@/services/LdapServerService.class";
import {entraAccountProviderService} from "@/services/EntraAccountProviderService.class";
import {entraConnectionService} from "@/services/EntraConnectionService.class";
import {entraGroupService} from "@/services/EntraGroupService.class";
import BaseTable from '@/components/common/BaseTable';
import BaseDeleteDialog from '@/components/common/BaseDeleteDialog';
import BaseTestConnectionButton from '@/components/common/BaseTestConnectionButton';
import {createNamespacedHelpers} from 'vuex';

const authStateMappers = createNamespacedHelpers('auth');

export default {
  name: "CustomerForm",
  components: {BaseTable, BaseDeleteDialog, BaseTestConnectionButton},
  props: {
    loading: {default: false},
    customer: {default: null},
    mode: {default: 'edit'},
    error: {default: ''}
  },
  mounted() {
    if (this.mode === 'create') {
      this.internalCustomer = {};
      this.formRules = generateRules.bind(this)(this.$t);
      this.formRules.mail = this.formRules.mail.map(rule => ({...rule, required: false}));
    }
  },
  data() {
    return {
      internalCustomer: null,
      formRules: null,
      ldapProviders: [],
      ldapServers: [],
      ldapLoading: false,
      currentProvider: null,
      showConnectionModal: false,
      showDeleteConnectionModal: false,
      currentProviderForDeletion: null,
      // Entra
      entraProviders: [],
      entraConnections: [],
      entraGroups: [],
      entraLoading: false,
      entraGroupsLoading: false,
      entraGroupsSyncing: false,
      currentEntraProvider: null,
      showEntraModal: false,
      showEntraDeleteModal: false,
      currentEntraProviderForDeletion: null,
    };
  },
  computed: {
    roles() {
      return [
        {value: 'USER', label: 'User', desc: this.$t('settings.roleDescriptions.USER'), icon: 'el-icon-user'},
        {
          value: 'REPORTER',
          label: 'Reporter',
          desc: this.$t('settings.roleDescriptions.REPORTER'),
          icon: 'el-icon-document'
        },
        {value: 'ADMIN', label: 'Admin', desc: this.$t('settings.roleDescriptions.ADMIN'), icon: 'el-icon-key'},
      ];
    },
    connectionCountLabel() {
      return this.ldapProviders.length === 1
        ? this.$t('settings.connectionsSingular')
        : this.$t('settings.connectionsPlural');
    },
    entraCountLabel() {
      return this.entraProviders.length === 1
        ? this.$t('settings.connectionsSingular')
        : this.$t('settings.connectionsPlural');
    },
    ...authStateMappers.mapGetters({isSuperAdmin: 'isSuperAdmin'}),
  },
  watch: {
    customer() {
      this.internalCustomer = {...this.customer};
      this.formRules = generateRules.bind(this)(this.$t);
      this.formRules.mail = this.formRules.mail.map(rule => ({...rule, required: false}));
      if (this.mode === 'edit') {
        this.loadLdapData();
        this.loadEntraData();
      }
    }
  },
  methods: {
    roleTagType(role) {
      if (role === 'ADMIN') return 'warning';
      if (role === 'REPORTER') return 'info';
      return '';
    },
    syncedCount(row) {
      return row && row.synchronizedUserCount != null ? row.synchronizedUserCount : 0;
    },

    ldapErrorMessage(e) {
      const status = e && e.response && e.response.status;
      if (status === 409) {
        return this.$t('settings.adConnections.ldapServerErrors.alreadyExists');
      }
      if (status === 400 || status === 412) {
        return this.$t('settings.adConnections.ldapServerErrors.noMatchingUser');
      }
      return this.$t('settings.adConnections.ldapServerErrors.generic');
    },
    entraErrorMessage(e, context = 'provider') {
      return getEntraErrorMessage(this.$t.bind(this), e, context);
    },
    validateLdapProviderRequest() {
      return ldapAccountProviderService.validate({
        id: this.currentProvider.id,
        customerId: this.internalCustomer.id,
        serverId: this.currentProvider.serverId,
        distinguishedName: this.currentProvider.distinguishedName,
        role: this.currentProvider.role,
      });
    },
    loadLdapData() {
      this.ldapLoading = true;
      Promise.all([
        ldapAccountProviderService.getByCustomer(this.internalCustomer.id),
        ldapServerService.getAll()
      ]).then(([providersRes, serversRes]) => {
        this.ldapProviders = providersRes.data;
        this.ldapServers = serversRes.data;
      }).catch(() => {
        this.$message.error(this.$t('settings.genericHttpError'));
      }).finally(() => {
        this.ldapLoading = false;
      });
    },
    async onGeneralFormSubmit() {
      try {
        await this.$refs['general-form'].validate();
        this.$emit('submit', this.internalCustomer);
      } catch (e) {
      }
    },
    onRowClick(provider) {
      this.currentProvider = {
        distinguishedName: '',
        role: null,
        ...provider,
        serverId: provider.serverId || (provider.server && provider.server.id) || null
      };
      this.showConnectionModal = true;
    },
    onCreateConnectionClick() {
      this.currentProvider = {serverId: null, distinguishedName: '', role: null};
      this.showConnectionModal = true;
    },
    onDialogClose() {
      this.currentProvider = null;
      this.currentProviderForDeletion = null;
    },
    onAdConnectionDeleteIntentionClick(provider) {
      this.currentProviderForDeletion = provider;
      this.showDeleteConnectionModal = true;
    },
    async onConnectionFormSubmit() {
      try {
        await this.$refs['connection-form'].validate();
        this.ldapLoading = true;
        if (this.currentProvider.id) {
          await ldapAccountProviderService.patch({
            ...this.currentProvider,
            customerId: this.internalCustomer.id
          });
          this.$message.success(this.$t('settings.adConnectionUpdated'));
        } else {
          await ldapAccountProviderService.create({
            ...this.currentProvider,
            customerId: this.internalCustomer.id
          });
          this.$message.success(this.$t('settings.adConnectionAdded'));
        }
        this.showConnectionModal = false;
        this.loadLdapData();
      } catch (e) {
        if (e !== false) this.$message.error(this.ldapErrorMessage(e));
      } finally {
        this.ldapLoading = false;
      }
    },
    async onConnectionDeleteConfirmation() {
      try {
        this.ldapLoading = true;
        await ldapAccountProviderService.delete(this.currentProviderForDeletion.id);
        this.$message.success(this.$t('settings.adConnectionDeleted'));
        this.showDeleteConnectionModal = false;
        this.loadLdapData();
      } catch (e) {
        this.$message.error(this.$t('settings.genericHttpError'));
      } finally {
        this.ldapLoading = false;
      }
    },

    // Entra
    entraConnectionLabel(provider) {
      if (provider.connection && provider.connection.entraTenantId) {
        return provider.connection.entraTenantId;
      }
      const match = this.entraConnections.find(c => c.id === provider.connectionId);
      return match ? match.entraTenantId : provider.connectionId;
    },
    entraTenantName(provider) {
      const connection = provider.connection
        || this.entraConnections.find(c => c.id === provider.connectionId);
      return connection && connection.name ? connection.name : '—';
    },
    entraTenantLabel(provider) {
      const connection = provider.connection
        || this.entraConnections.find(c => c.id === provider.connectionId);
      return connection && connection.tenant && connection.tenant.name
        ? connection.tenant.name
        : '—';
    },
    entraGroupLabel(provider) {
      if (provider.group && provider.group.displayName) {
        return provider.group.displayName;
      }
      const match = this.entraGroups.find(group => group.id === provider.groupId);
      return match ? (match.displayName || match.name) : (provider.groupId || '-');
    },
    loadEntraData() {
      this.entraLoading = true;
      Promise.all([
        entraAccountProviderService.getByCustomer(this.internalCustomer.id),
        entraConnectionService.getAll()
      ]).then(([providersRes, connectionsRes]) => {
        this.entraProviders = providersRes.data;
        this.entraConnections = connectionsRes.data;
      }).catch((e) => {
        this.$message.error(this.entraErrorMessage(e));
      }).finally(() => {
        this.entraLoading = false;
      });
    },
    loadEntraGroups(connectionId) {
      if (!connectionId) {
        this.entraGroups = [];
        return;
      }
      this.entraGroupsLoading = true;
      return entraGroupService.getByConnection(connectionId)
        .then((response) => {
          this.entraGroups = response.data;
        })
        .catch((e) => {
          this.entraGroups = [];
          this.$message.error(this.entraErrorMessage(e));
        })
        .finally(() => {
          this.entraGroupsLoading = false;
        });
    },
    async onSyncEntraGroupsClick() {
      const connectionId = this.currentEntraProvider && this.currentEntraProvider.connectionId;
      if (!connectionId) return;

      this.entraGroupsSyncing = true;
      try {
        const {data} = await entraGroupService.syncConnection(connectionId);

        await this.loadEntraGroups(connectionId);

        const selected = this.currentEntraProvider.groupId;
        if (selected && !this.entraGroups.some(group => group.id === selected)) {
          this.currentEntraProvider.groupId = null;
        }

        const count = Array.isArray(data) ? data.length : (data && data.count);
        this.$message.success(
          count != null
            ? this.$t('settings.entraGroupSyncSuccessCount', {count})
            : this.$t('settings.entraGroupSyncSuccess')
        );
      } catch (e) {
        this.$message.error(this.entraErrorMessage(e));
      } finally {
        this.entraGroupsSyncing = false;
      }
    },
    onEntraConnectionChange(connectionId) {
      this.currentEntraProvider.groupId = null;
      this.entraGroups = [];
      this.loadEntraGroups(connectionId);
    },
    onEntraRowClick(provider) {
      this.currentEntraProvider = {
        ...provider,
        connectionId: provider.connection ? provider.connection.id : null,
        groupId: provider.group ? provider.group.id : null,
      };
      const linkedGroup = provider.group || null;
      this.entraGroups = linkedGroup ? [linkedGroup] : [];
      if (this.currentEntraProvider.connectionId) {
        this.loadEntraGroups(this.currentEntraProvider.connectionId).then(() => {
          if (linkedGroup && !this.entraGroups.some(g => g.id === linkedGroup.id)) {
            this.entraGroups = [linkedGroup, ...this.entraGroups];
          }
        });
      }
      this.showEntraModal = true;
    },
    onCreateEntraProviderClick() {
      this.currentEntraProvider = {connectionId: null, groupId: null, role: null};
      this.entraGroups = [];
      this.showEntraModal = true;
    },
    onEntraDialogClose() {
      this.currentEntraProvider = null;
      this.currentEntraProviderForDeletion = null;
      this.entraGroups = [];
      this.entraGroupsSyncing = false;
    },
    onEntraProviderDeleteIntentionClick(provider) {
      this.currentEntraProviderForDeletion = provider;
      this.showEntraDeleteModal = true;
    },
    entraProviderTestError(e) {
      return this.entraErrorMessage(e, 'provider');
    },
    validateEntraProviderRequest() {
      return entraAccountProviderService.validate({
        id: this.currentEntraProvider.id,
        customerId: this.internalCustomer.id,
        connectionId: this.currentEntraProvider.connectionId,
        groupId: this.currentEntraProvider.groupId,
        role: this.currentEntraProvider.role,
      });
    },
    async onEntraProviderFormSubmit() {
      try {
        await this.$refs['entra-provider-form'].validate();
        this.entraLoading = true;
        if (this.currentEntraProvider.id) {
          await entraAccountProviderService.patch({
            ...this.currentEntraProvider,
            customerId: this.internalCustomer.id
          });
          this.$message.success(this.$t('settings.entraConnectionUpdated'));
        } else {
          await entraAccountProviderService.create({
            ...this.currentEntraProvider,
            customerId: this.internalCustomer.id
          });
          this.$message.success(this.$t('settings.entraConnectionAdded'));
        }
        this.showEntraModal = false;
        this.loadEntraData();
      } catch (e) {
        if (e !== false) this.$message.error(this.entraErrorMessage(e));
      } finally {
        this.entraLoading = false;
      }
    },
    async onEntraProviderDeleteConfirmation() {
      try {
        this.entraLoading = true;
        await entraAccountProviderService.delete(this.currentEntraProviderForDeletion.id);
        this.$message.success(this.$t('settings.entraConnectionDeleted'));
        this.showEntraDeleteModal = false;
        this.loadEntraData();
      } catch (e) {
        this.$message.error(this.entraErrorMessage(e));
      } finally {
        this.entraLoading = false;
      }
    },
  }
};
</script>

<style lang="scss">
.ad-connection-table-row {
  cursor: pointer;
}

.entra-connection-select {
  .el-select-dropdown__item {
    height: auto;
    line-height: 1.4;
    padding-top: 6px;
    padding-bottom: 6px;
  }
}

.customer-connection-dialog {
  border-radius: 14px !important;
  overflow: hidden;

  .el-dialog__header {
    padding: 0;
    border-bottom: 1px solid #f0f0f5;
  }

  .el-dialog__body {
    padding: 0;
  }

  .el-dialog__footer {
    padding: 0;
  }
}
</style>

<style scoped lang="scss">
.section-icon {
  width: 32px;
  height: 32px;
  border-radius: 8px;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;

  i {
    color: #fff;
    font-size: 15px;
  }

  &--blue {
    background: linear-gradient(135deg, #409EFF, #2d7dd2);
  }

  &--purple {
    background: linear-gradient(135deg, #6366f1, #4f46e5);
  }
}

.settings-section {
  background: #fff;
  border: 1px solid var(--border);
  border-radius: 12px;
  overflow: hidden;
  box-shadow: 0 1px 4px rgba(0, 0, 0, .04);
  margin-bottom: 20px;

  &__header {
    display: flex;
    align-items: center;
    gap: 10px;
    padding: 16px 20px;
    border-bottom: 1px solid var(--border);
    background: #f8f9fc;
  }

  &__title {
    margin: 0;
    font-size: 14px;
    font-weight: 600;
    color: var(--text-primary);
  }
}

.customer-edit-form {
  padding: 24px 20px 20px;
}

.form-grid {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 0 24px;

  @media (max-width: 800px) {
    grid-template-columns: 1fr;
  }
}

:deep(.el-form-item__label) {
  font-size: 13px;
  font-weight: 500;
  color: var(--text-secondary);
  padding-bottom: 6px;
  line-height: 1.4;
}

:deep(.el-input__inner) {
  border-radius: 8px;
  border-color: var(--border);
  height: 40px;
  font-size: 13.5px;
  color: var(--text-primary);
  transition: border-color .2s, box-shadow .2s;

  &:focus {
    border-color: var(--primary);
    box-shadow: 0 0 0 3px rgba(64, 158, 255, .12);
  }
}

:deep(.el-form-item) {
  margin-bottom: 20px;
}

.checkbox-item {
  padding: 0 0 4px;

  :deep(.el-checkbox__label) {
    font-size: 13.5px;
    color: var(--text-primary);
  }
}

.form-actions {
  padding-top: 4px;
}

.btn-submit {
  border-radius: 8px;
  font-weight: 500;
  min-width: 110px;
}

.ldap-section {
  background: #fff;
  border: 1px solid var(--border);
  border-radius: 12px;
  overflow: hidden;
  box-shadow: 0 1px 4px rgba(0, 0, 0, .04);

  &__header {
    display: flex;
    align-items: center;
    gap: 10px;
    padding: 16px 20px;
    border-bottom: 1px solid var(--border);
    background: #f8f9fc;
  }

  &__title-wrap {
    flex: 1;
    display: flex;
    flex-direction: column;
    gap: 2px;
  }

  &__title {
    margin: 0;
    font-size: 14px;
    font-weight: 600;
    color: var(--text-primary);
  }

  &__count {
    font-size: 12px;
    color: var(--text-secondary);
  }
}

.connection-badge {
  border-radius: 20px;
  font-size: 11px;
  font-weight: 600;
}

.btn-add-connection {
  border-radius: 7px;
  font-size: 12.5px;
  font-weight: 500;
  height: auto;
  padding: 6px 12px;
}

.ldap-empty {
  display: flex;
  flex-direction: column;
  align-items: center;
  text-align: center;
  padding: 48px 24px;
  gap: 8px;

  &__icon {
    width: 52px;
    height: 52px;
    border-radius: 50%;
    background: #eef4ff;
    display: flex;
    align-items: center;
    justify-content: center;
    margin-bottom: 8px;

    i {
      font-size: 22px;
      color: var(--primary);
    }
  }

  &__title {
    margin: 0;
    font-size: 15px;
    font-weight: 600;
    color: var(--text-primary);
  }

  &__subtitle {
    margin: 0 0 12px;
    font-size: 13px;
    color: var(--text-secondary);
    max-width: 420px;
    line-height: 1.5;
  }
}

.cell-url {
  display: flex;
  align-items: center;
  gap: 7px;

  &__icon {
    color: var(--primary);
    font-size: 13px;
    flex-shrink: 0;
  }

  &__text {
    font-family: 'SFMono-Regular', Consolas, monospace;
    font-size: 12.5px;
    color: #444;
  }
}

.cell-dn {
  font-size: 12.5px;
  color: var(--text-secondary);
  word-break: break-all;
}

.role-tag {
  border-radius: 20px;
  font-size: 11px;
  font-weight: 600;
}

.cell-sync {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 6px;
  font-size: 13px;
  color: var(--text-secondary);

  &__icon {
    color: #bbb;
    font-size: 13px;
  }
}

.action-btns {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 6px;
}

.connection-dialog__header {
  display: flex;
  align-items: center;
  gap: 14px;
  padding: 20px 24px;
}

.connection-dialog__icon {
  width: 42px;
  height: 42px;
  border-radius: 10px;
  background: linear-gradient(135deg, #6366f1, #4f46e5);
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;

  i {
    color: #fff;
    font-size: 18px;
  }
}

.connection-dialog__title-wrap {
  flex: 1;
  display: flex;
  flex-direction: column;
  gap: 2px;
}

.connection-dialog__title {
  font-size: 16px;
  font-weight: 600;
  color: var(--text-primary);
  line-height: 1.3;
}

.connection-dialog__subtitle {
  font-size: 12.5px;
  color: var(--text-secondary);
}

.connection-dialog__close {
  background: none;
  border: none;
  cursor: pointer;
  color: #909399;
  font-size: 18px;
  padding: 4px;
  border-radius: 6px;
  line-height: 1;
  transition: color .2s;

  &:hover {
    color: var(--text-primary);
  }
}

.connection-dialog__footer {
  display: flex;
  justify-content: flex-end;
  gap: 10px;
  padding: 16px 24px;
  border-top: 1px solid var(--border);

  &--split {
    justify-content: space-between;
    align-items: center;
  }
}

.connection-dialog__footer-left {
  display: inline-flex;
}

.connection-dialog__footer-right {
  display: inline-flex;
  gap: 10px;
}

.dialog-body {
  padding: 20px 24px 4px;

  :deep(.el-form-item__label) {
    font-size: 13px;
    font-weight: 500;
    color: var(--text-secondary);
  }

  :deep(.el-select .el-input__inner) {
    height: 40px;
    border-radius: 8px;
    border-color: var(--border);
  }
}

.field-hint {
  margin: 5px 0 0;
  font-size: 11px;
  color: #c8c8c8;
  line-height: 1.4;
}

.group-select-row {
  display: flex;
  align-items: center;
  gap: 8px;

  &__select {
    flex: 1 1 auto;
    min-width: 0;
  }

  &__sync {
    flex: 0 0 auto;
    width: 40px;
    height: 40px;
    padding: 0;

    i {
      font-size: 15px;
    }

    &:not(.is-disabled):hover,
    &:not(.is-disabled):focus {
      color: var(--entra-dark);
      border-color: var(--entra-dark);
      background: transparent;
    }
  }
}

.server-option {
  display: flex;
  align-items: center;
  gap: 8px;

  &__icon {
    color: #aaa;
    font-size: 13px;
  }

  &__url {
    flex: 1;
    font-size: 13px;
    font-family: 'SFMono-Regular', Consolas, monospace;
  }

  &__badge {
    border-radius: 4px;
    font-size: 10px;
    font-weight: 700;
  }

  &__text {
    display: flex;
    flex-direction: column;
    line-height: 1.3;
  }

  &__title {
    font-size: 13px;
    font-weight: 500;
    color: var(--text-primary);
  }

  &__subtitle {
    font-size: 11px;
    color: var(--text-secondary);
  }
}

.role-cards {
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 10px;
}

.role-card {
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 4px;
  padding: 14px 14px 12px;
  border: 1.5px solid var(--border);
  border-radius: 10px;
  cursor: pointer;
  transition: border-color .2s, background .2s;

  &:hover {
    border-color: var(--primary);
    background: #f5f9ff;
  }

  &--active {
    border-color: var(--primary);
    background: #eef4ff;
  }

  &__icon {
    font-size: 18px;
    color: var(--text-secondary);
    margin-bottom: 4px;
  }

  &--active &__icon {
    color: var(--primary);
  }

  &__name {
    font-size: 13.5px;
    font-weight: 600;
    color: var(--text-primary);
  }

  &__desc {
    font-size: 11.5px;
    color: var(--text-secondary);
    line-height: 1.3;
  }
}
</style>

<style scoped lang="scss">
.section-icon--entra {
  background: linear-gradient(135deg, var(--entra), var(--entra-dark));
}

.entra-section {
  margin-top: 20px;
}

.ldap-empty__icon--entra {
  background: var(--entra-bg);

  i {
    color: var(--entra-dark);
  }
}

.cell-url__icon--entra {
  color: var(--entra-dark);
}

.connection-dialog__icon--entra {
  background: linear-gradient(135deg, var(--entra), var(--entra-dark));
}
</style>
